import React from "react";
import { Button } from "../ui";

/**
 * ReportPagination — Standalone Pagination Controls Box
 * 
 * Props:
 * - currentPage: 1-based current page number
 * - totalPages: Total number of pages
 * - totalCount: Total number of records
 * - rowsPerPage: Current limit / page size
 * - onPageChange: Callback with (newPage)
 * - onRowsPerPageChange: Callback with (newRowsPerPage)
 * - pageSizeOptions: Array of options (default: [25, 50, 100, 200])
 */
const ReportPagination = ({
  currentPage = 1,
  totalPages = 1,
  totalCount,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  pageSizeOptions = [25, 50, 100, 200],
  className = "",
}) => {
  const indexOfFirst = (currentPage - 1) * rowsPerPage;

  return (
    <div
      className={[
        "flex items-center gap-3 bg-white px-3 py-2 rounded-xl border border-border-light shadow-sm",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Rows per page */}
      <div className="flex items-center gap-2 text-xs text-ink-500">
        <span>Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
          className="bg-transparent border border-border-medium rounded px-1.5 py-1 focus:outline-none font-semibold text-ink-700 text-xs"
        >
          {pageSizeOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* Record Counter (optional if totalCount provided) */}
      {typeof totalCount === "number" && totalCount > 0 && (
        <span className="text-xs text-ink-500">
          {indexOfFirst + 1}–{Math.min(indexOfFirst + rowsPerPage, totalCount)} of {totalCount}
        </span>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange?.(1)}
          disabled={currentPage <= 1}
        >
          First
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange?.(Math.max(currentPage - 1, 1))}
          disabled={currentPage <= 1}
        >
          Previous
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange?.(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage >= totalPages}
        >
          Next
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange?.(totalPages)}
          disabled={currentPage >= totalPages}
        >
          Last
        </Button>
      </div>
    </div>
  );
};

export default ReportPagination;
